import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useFormik } from 'formik';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import Loading from '../components/Loading';
import Input from '../components/form/Input';
import Layout from './Layout';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

export const getAllUsers = ({ page = 0, per_page = 20, q = null, provider_id = undefined }) => {
    return axios
        .request({
            url: `/authorized/users`,
            method: 'GET',
            params: {
                page,
                per_page,
                q,
                provider_id
            }
        })
        .then((resp) => resp.data);
};

export const deleteUser = (username) => {
    let safeUsername = window.encodeURIComponent(username);
    return axios.request({
        url: `/authorized/users/${safeUsername}`,
        method: 'DELETE'
    });
};

const resendConf = (email) => {
    axios.request({
        url: `/unauthorized/resendconfirmation`,
        method: 'POST',
        data: {
            username: email
        }
    });
};

const confirmEmail = (username) => {
    axios.request({
        url: `/unauthorized/confirmemail`,
        method: 'POST',
        data: {
            username
        }
    });
};

const Users = ({ location = {} }) => {
    const session = useSelector((state) => state.session);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [results, setResults] = useState([]);
    const [page, setPage] = useState(1);
    const [pageCount, setPageCount] = useState();

    const history = useHistory();

    const { values, setFieldValue, handleSubmit, handleReset } = useFormik({
        initialValues: {
            search: ''
        },
        onReset: async () => {
            setPage(1);
            await loadUsers({ page: 1 });
        },
        onSubmit: async (values) => {
            if (!values?.search) return;
            setPage(1);
            await loadUsers({ query: values.search, page: 1 });
        }
    });

    const loadUsers = useCallback(async ({ query, page = 1 }) => {
        const perPage = 20;
        setIsLoading(true);
        try {
            const resp = await getAllUsers({
                page: page,
                per_page: perPage,
                order_by: 'name:asc',
                q: query
            });
            setResults(resp.results);
            setPageCount(Math.ceil(resp.total / perPage));
        } catch {
            setError('Error Loading Users.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (session?.isLoading) return;
        if (!session.isLoading && !session.isAdmin) {
            history.push('/home');
            return;
        }
        loadUsers({ page: 1 });
    }, [session, loadUsers, history]);

    const handleDeleteUser = async (username) => {
        try {
            await deleteUser(username);
            await loadUsers();
            toast.success(`${username} Removed.`);
        } catch {
            toast.error('Error Removing User.');
        }
    };

    const nextPage = async () => {
        setPage(page + 1);
        await loadUsers({ page: page + 1 });
    };

    const prevPage = async () => {
        setPage(page - 1);
        await loadUsers({ page: page - 1 });
    };

    return (
        <Layout>
            <div>
                <div className="page-header">
                    <h1 className="collapse">Users</h1>
                </div>

                <div className="row mb-5">
                    <div className="col-xs-12 col-sm">
                        <form onSubmit={handleSubmit} onReset={handleReset} noValidate>
                            <Input
                                id="search"
                                value={values.search}
                                onChange={(id, val) => setFieldValue('search', val)}
                                placeholder="Search First Name, Last Name, or Email."
                                append={
                                    <>
                                        {values.search && (
                                            <button className="btn btn-sm btn-muted" type="reset">
                                                &times;
                                            </button>
                                        )}
                                        <button className="btn btn-sm btn-secondary" type="submit">
                                            Search
                                        </button>
                                    </>
                                }
                                small
                                collapse
                            />
                        </form>
                    </div>
                        <div className="col">
                            <Link to="/users/add" className="btn btn-primary btn-sm">
                                Add User
                            </Link>
                        </div>
                </div>
                <div>
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th>Name</th>
                                <th>Provider</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && (
                                <tr>
                                    <td colSpan={4}>
                                        <Loading />
                                    </td>
                                </tr>
                            )}
                            {!isLoading && !results.length && (
                                <tr>
                                    <td colSpan={4}>No Users</td>
                                </tr>
                            )}
                            {!isLoading && !!error && (
                                <tr>
                                    <td colSpan={4}>{error}</td>
                                </tr>
                            )}
                            {!isLoading &&
                                !!results?.length &&
                                results.map((user) => (
                                    <tr key={user.username}>
                                        <td>
                                            <Link to={`/users/${user.username}`}>{user.email}</Link>
                                        </td>
                                        <td>
                                            {user.firstname} {user.lastname}
                                        </td>
                                        <td>{user.provider ? user.provider.name : 'None'}</td>
                                        <td>
                                            {user.username !== session.session['cognito:username'] && (
                                                <button
                                                    className="btn btn-sm btn-danger btn-sm"
                                                    onClick={(e) => handleDeleteUser(user.username)}
                                                >
                                                    Delete
                                                </button>
                                            )}
                                            {user.cognito_data.user_status === 'FORCE_CHANGE_PASSWORD' && (
                                                <button className="btn btn-sm" onClick={(e) => resendConf(user.email)}>
                                                    Resend Confirmation
                                                </button>
                                            )}
                                            {!user.cognito_data.email_verified && (
                                                <button
                                                    className="btn btn-sm"
                                                    onClick={(e) => confirmEmail(user.username)}
                                                >
                                                    Verify Email
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                    {!!results.length && !isLoading && (
                        <div className="sales-pagination">
                            <button className="btn btn-sm" onClick={prevPage} disabled={page === 1}>
                                Prev
                            </button>
                            <span>
                                Page {page} of {pageCount}
                            </span>
                            <button className="btn btn-sm" onClick={nextPage} disabled={page >= pageCount}>
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default Users;
